import { isElementValueEmpty, isServerAssignedElement } from '../../utils/elements';
import { extractLookupIds, resolveLookupText } from '../../utils/lookups';
import { markdownToPlainText } from '../../utils/markdown';
import { elementLabel, formatValue } from './itemValueDisplay';
import { sameValue } from './staleConflict';

/**
 * What a draft holds, and what applying one does to the form — the words
 * the new-item form's draft chip popover and its "Replace what is in the
 * form?" dialog are made of (keepr-api SCHEMA.md § Item drafts). Pure: the
 * form hands in the card's elements, their new-item defaults, and the two
 * sides; nothing here touches the server or the DOM (bar markdown's text).
 *
 * A row per element the draft or the change touches, in the card's element
 * order. (A draft saved before tags were retired may still hold a `tags`
 * list, and one saved before comments replaced the Notes box a `notes` text;
 * nothing here reads either, because nothing would save it — docs/plans/
 * tags.md §4.1, KPE-005.) Server-assigned elements (calculated,
 * sequence numbers) are never listed: nobody typed them.
 */

// A value no longer than this reads in full; longer ones are cut at a word.
const TEXT_LIMIT = 120;

const cut = (text) => {
	const flat = String(text ?? '').replace(/\s+/g, ' ').trim();
	if (flat.length <= TEXT_LIMIT) return flat;
	const head = flat.slice(0, TEXT_LIMIT);
	const space = head.lastIndexOf(' ');
	return `${(space > TEXT_LIMIT * 0.6 ? head.slice(0, space) : head).trim()}…`;
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * One value as the popover and the dialog quote it: the element's own
 * formatting, never a raw id or `[object Object]`, cut to a readable length.
 * '' for an empty value.
 */
export function draftValueText(def, value, { resolveLookupValue } = {}) {
	if (isElementValueEmpty(def, value)) return '';
	const type = def?.dataType;
	if (type === 'card-lookup') {
		const titles = resolveLookupText(resolveLookupValue, value);
		if (titles) return cut(titles);
		const count = extractLookupIds(value).length;
		return count ? plural(count, 'linked item', 'linked items') : '';
	}
	if (type === 'user') {
		const count = Array.isArray(value) ? value.filter(Boolean).length : 1;
		return plural(count, 'person', 'people');
	}
	if (type === 'rich-text') return cut(markdownToPlainText(String(value)));
	const one = (entry) => {
		let text;
		try { text = formatValue(def, entry, { resolveLookupValue }); } catch { text = ''; }
		if (typeof text === 'string' || typeof text === 'number') return String(text);
		if (typeof entry === 'string' || typeof entry === 'number') return String(entry);
		return '';
	};
	let text = one(value);
	if (!text && Array.isArray(value)) text = value.map(one).filter(Boolean).join(', ');
	return cut(text) || 'Filled in';
}

/**
 * The fields a draft sets, for the chip's popover: `[{ key, label, def,
 * value }]`, only the ones holding something a blank form would not (a
 * value equal to the card's new-item default is not something the person
 * wrote — a Yes/No at No, a date at today).
 */
export function draftContentRows({ elementDefs = [], defaults = {}, draft = {} }) {
	const rows = [];
	const seen = new Set();
	const elements = draft?.elements && typeof draft.elements === 'object' ? draft.elements : {};
	elementDefs.forEach(def => {
		const name = def?.name;
		if (!name || seen.has(name) || isServerAssignedElement(def)) return;
		seen.add(name);
		const value = elements[name];
		if (isElementValueEmpty(def, value)) return;
		if (Object.prototype.hasOwnProperty.call(defaults, name) && sameValue(value, defaults[name])) return;
		rows.push({ key: name, label: elementLabel(def), def, value });
	});
	return rows;
}

/**
 * What applying a draft does to the form, field by field. `current` and
 * `incoming` are `{ elements }` — `incoming` as the
 * form will hold it (every element back to its default, then the draft's
 * values: a draft REPLACES the form, it never merges). Identical values are
 * left out. Each row: `{ key, label, def, from, to, kind }`, where kind is
 *
 *   'fills'    — the form holds nothing there (or only the card default):
 *                nothing of the person's is touched;
 *   'replaces' — the form holds a value, and the draft's is different;
 *   'clears'   — the form holds a value, and the draft has none.
 *
 * `replaces` and `clears` are the rows where what the form holds now is
 * lost unless it is kept as a draft (`losesSomething`). `seeded` names the
 * elements whose value the form put there itself and nobody has touched
 * since (a "default to me" lookup, a date default) — a default, whatever it
 * holds.
 */
export function draftReplaceRows({ elementDefs = [], defaults = {}, current = {}, incoming = {}, seeded = null }) {
	const rows = [];
	const seen = new Set();
	const kindOf = (fromEmpty, toEmpty) => {
		if (fromEmpty) return 'fills';
		return toEmpty ? 'clears' : 'replaces';
	};
	elementDefs.forEach(def => {
		const name = def?.name;
		if (!name || seen.has(name) || isServerAssignedElement(def)) return;
		seen.add(name);
		const from = current?.elements?.[name];
		const to = incoming?.elements?.[name];
		if (sameValue(from, to)) return;
		const hasDefault = Object.prototype.hasOwnProperty.call(defaults, name);
		const fromEmpty = isElementValueEmpty(def, from)
			|| (hasDefault && sameValue(from, defaults[name]))
			|| Boolean(seeded?.has?.(name));
		const toEmpty = isElementValueEmpty(def, to);
		// A default giving way to nothing: neither side is anyone's typing.
		if (fromEmpty && toEmpty) return;
		rows.push({ key: name, label: elementLabel(def), def, from, to, kind: kindOf(fromEmpty, toEmpty) });
	});

	return rows;
}

/** Does applying lose anything the form holds now? */
export const losesSomething = rows => (rows || []).some(row => row.kind === 'replaces' || row.kind === 'clears');

/** A popover row's value as text. */
export function contentRowText(row, options) {
	return draftValueText(row?.def, row?.value, options);
}

/** A row's side as text: the element's formatting of that side's value. */
export function rowSideText(row, side, options) {
	return draftValueText(row?.def, side === 'from' ? row?.from : row?.to, options);
}
